import React from 'react';
import { PrintableDocument, PrintStyles } from '../ui/PrintableDocument';
import { StatusBadge } from '../ui/StatusBadge';
import { formatCurrency, formatDate } from '../../lib/utils';

interface PrintableQuoteProps {
  quote: any;
  isVisible?: boolean;
}

export const PrintableQuote: React.FC<PrintableQuoteProps> = ({
  quote,
  isVisible = false
}) => {
  if (!quote) return null;

  return (
    <>
      <PrintStyles />
      <div className={`${isVisible ? 'block' : 'hidden'} print:block`}>
        <PrintableDocument type="quote" data={quote}>
          {/* Quote Details Section */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            {/* Quote To */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                QUOTE TO
              </h3>
              <div className="space-y-2">
                <p className="font-semibold text-gray-900">{quote.client?.name}</p>
                {quote.client?.company && (
                  <p className="text-gray-700">{quote.client.company}</p>
                )}
                <p className="text-gray-600">{quote.client?.email}</p>
                {quote.client?.phone && (
                  <p className="text-gray-600">{quote.client.phone}</p>
                )}
                {quote.client?.address && (
                  <p className="text-gray-600 whitespace-pre-line">{quote.client.address}</p>
                )}
                {quote.client?.gstin && (
                  <p className="text-gray-600">GSTIN: {quote.client.gstin}</p>
                )}
              </div>
            </div>

            {/* Quote Info */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                QUOTE DETAILS
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Quote Date:</span>
                  <span className="font-medium">{formatDate(quote.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Valid Until:</span>
                  <span className="font-medium">{formatDate(quote.valid_until)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <StatusBadge status={quote.status} />
                </div>
                {quote.notes && (
                  <div className="mt-4">
                    <span className="text-gray-600 block mb-2">Notes:</span>
                    <p className="text-gray-700 text-sm bg-gray-50 p-3 rounded border">
                      {quote.notes}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quote Items Table */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">ITEMS</h3>
            <table className="document-table w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left py-3 px-4 font-semibold">Description</th>
                  <th className="text-center py-3 px-4 font-semibold w-20">Qty</th>
                  <th className="text-right py-3 px-4 font-semibold w-24">Rate</th>
                  <th className="text-right py-3 px-4 font-semibold w-24">Amount</th>
                </tr>
              </thead>
              <tbody>
                {quote.quote_items?.map((item: any, index: number) => (
                  <tr key={item.id || index} className="border-b border-gray-100">
                    <td className="py-3 px-4">
                      <div className="font-medium text-gray-900">{item.description}</div>
                    </td>
                    <td className="py-3 px-4 text-center">{Number(item.quantity)}</td>
                    <td className="py-3 px-4 text-right">{formatCurrency(Number(item.rate))}</td>
                    <td className="py-3 px-4 text-right font-semibold">
                      {formatCurrency(Number(item.amount))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals Section */}
          <div className="flex justify-end">
            <div className="w-80">
              <div className="total-section bg-gray-50 border border-gray-200 rounded-lg p-6">
                <div className="space-y-3">
                  <div className="flex justify-between text-gray-700">
                    <span>Subtotal:</span>
                    <span className="font-medium">{formatCurrency(Number(quote.subtotal))}</span>
                  </div>
                  <div className="flex justify-between text-gray-700">
                    <span>Tax ({Number(quote.tax_rate)}%):</span>
                    <span className="font-medium">{formatCurrency(Number(quote.tax_amount))}</span>
                  </div>
                  <div className="border-t border-gray-300 pt-3">
                    <div className="flex justify-between text-xl font-bold text-gray-900">
                      <span>Total:</span>
                      <span>{formatCurrency(Number(quote.total))}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quote Terms */}
          <div className="mt-8 p-4 bg-green-50 border border-green-200 rounded-lg">
            <h4 className="font-semibold text-green-900 mb-2">Quote Terms</h4>
            <p className="text-green-800 text-sm">
              This quote is valid until {formatDate(quote.valid_until)}. Prices are subject to change after this date.
              Please contact us to accept this quote and proceed with the work.
            </p>
          </div>
        </PrintableDocument>
      </div>
    </>
  );
};